import { useEffect, useState } from "react";
import { X, Shield, AlertCircle } from "lucide-react";
import api from "../api/client";

/**
 * Admin dialog for adding a new team member. Captures name + email and
 * (optionally) two security questions/answers. The server assigns the
 * generic temp password and forces a change on first login.
 */
export default function AddWorkerDialog({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [bank, setBank] = useState([]);
  const [bankError, setBankError] = useState(false);
  const [q1, setQ1] = useState({ question_id: "", answer: "" });
  const [q2, setQ2] = useState({ question_id: "", answer: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(true);

  useEffect(() => {
    if (!open || bank.length) return;
    api.get("/auth/security-questions/options")
      .then((r) => { setBank(r.data); setBankError(false); })
      .catch(() => { setBank([]); setBankError(true); });
  }, [open, bank.length]);

  useEffect(() => {
    if (bank.length >= 2 && !q1.question_id && !q2.question_id) {
      setQ1((p) => ({ ...p, question_id: bank[0].id }));
      setQ2((p) => ({ ...p, question_id: bank[1].id }));
    }
  }, [bank, q1.question_id, q2.question_id]);

  const reset = () => {
    setForm({ name: "", email: "" });
    setQ1({ question_id: bank[0]?.id || "", answer: "" });
    setQ2({ question_id: bank[1]?.id || "", answer: "" });
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    if (!name) return setError("Please enter the team member's name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Please enter a valid email address.");

    const a1 = q1.answer.trim();
    const a2 = q2.answer.trim();
    const payload = { name, email, send_welcome: sendWelcome };
    if (a1 || a2) {
      if (!a1 || !a2) return setError("Fill in both security answers, or leave both blank to skip.");
      if (!q1.question_id || !q2.question_id) return setError("Security questions did not load — leave the answers blank to skip.");
      if (q1.question_id === q2.question_id) return setError("Pick two different security questions.");
      payload.security_questions = [
        { question_id: q1.question_id, answer: a1 },
        { question_id: q2.question_id, answer: a2 },
      ];
    }

    setSaving(true);
    const ok = await onSubmit(payload);
    setSaving(false);
    if (ok) { reset(); onClose(); }
  };

  if (!open) return null;

  const q1OtherDisabled = (id) => id !== q1.question_id && id === q2.question_id;
  const q2OtherDisabled = (id) => id !== q2.question_id && id === q1.question_id;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={submit}
        noValidate
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-gray-300 max-w-lg w-full p-6 rounded-sm shadow-2xl my-6"
        data-testid="add-worker-form"
      >
        <div className="flex items-start justify-between mb-4">
          <h4 className="font-black text-xl tracking-tight">New Team Member</h4>
          <button type="button" onClick={onClose} data-testid="close-add-worker"><X className="w-5 h-5" /></button>
        </div>

        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Name</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="new-worker-name-input"
          className="w-full border border-gray-300 px-3 py-2 mb-3 rounded-sm text-sm"
        />

        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Email</label>
        <input
          type="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="new-worker-email-input"
          className="w-full border border-gray-300 px-3 py-2 mb-4 rounded-sm text-sm"
        />

        <div className="border-l-4 border-[#002FA7] bg-[#F0F4FF] px-3 py-2 mb-5 text-xs text-gray-700">
          A generic starter password will be assigned. The team member will be
          asked to set their own when they sign in for the first time.
        </div>

        <label className="flex items-start gap-2 mb-5 text-sm cursor-pointer" data-testid="send-welcome-label">
          <input
            type="checkbox"
            checked={sendWelcome}
            onChange={(e) => setSendWelcome(e.target.checked)}
            data-testid="send-welcome-checkbox"
            className="mt-0.5"
          />
          <span>
            Email them their login details now
            <span className="block text-xs text-gray-500">Sends the sign-in link, email and starter password from reports@… — untick to pass them on yourself.</span>
          </span>
        </label>

        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 mb-2">
          <Shield className="w-3 h-3" /> Security questions
          <span className="normal-case tracking-normal font-normal text-gray-400">(optional)</span>
        </div>
        <p className="text-[11px] text-gray-500 mb-3">
          Optionally record two answers to unlock the self-serve &ldquo;Forgot
          password&rdquo; flow. Leave blank to skip — they can be added later.
        </p>

        {bankError && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mb-3 rounded-sm" data-testid="sq-bank-error">
            Security questions couldn&apos;t be loaded. You can still add the team member without them.
          </div>
        )}

        <SQRow label="Question 1" bank={bank} state={q1} onChange={setQ1} isOtherDisabled={q1OtherDisabled} testId="sq1" />
        <SQRow label="Question 2" bank={bank} state={q2} onChange={setQ2} isOtherDisabled={q2OtherDisabled} testId="sq2" />

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-300 px-3 py-2 mt-2 rounded-sm"
            data-testid="add-worker-error"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="submit-add-worker"
          className="w-full bg-[#002FA7] text-white py-2.5 mt-3 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60"
        >
          {saving ? "CREATING…" : "CREATE TEAM MEMBER"}
        </button>
      </form>
    </div>
  );
}

function SQRow({ label, bank, state, onChange, isOtherDisabled, testId }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <select
        value={state.question_id}
        onChange={(e) => onChange({ ...state, question_id: e.target.value })}
        data-testid={`${testId}-question`}
        disabled={!bank.length}
        className="w-full border border-gray-300 px-3 py-2 mb-1 rounded-sm text-sm bg-white disabled:bg-gray-50"
      >
        {!bank.length && <option value="">Unavailable</option>}
        {bank.map((q) => (
          <option key={q.id} value={q.id} disabled={isOtherDisabled(q.id)}>
            {q.text}
          </option>
        ))}
      </select>
      <input
        placeholder="Answer (optional)"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        value={state.answer}
        onChange={(e) => onChange({ ...state, answer: e.target.value })}
        data-testid={`${testId}-answer`}
        className="w-full border border-gray-300 px-3 py-2 rounded-sm text-sm"
      />
    </div>
  );
}
